package eric.bitria.samples.scenes

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import io.github.erkko68.filament.compose.FilamentSceneView
import io.github.erkko68.filament.compose.orbitGestures
import io.github.erkko68.filament.compose.rememberOrbitCameraController
import io.github.erkko68.filament.compose.rememberSceneClock
import io.github.erkko68.filament.compose.scene.AntiAliasing
import io.github.erkko68.filament.compose.scene.Bloom
import io.github.erkko68.filament.compose.scene.Dithering
import io.github.erkko68.filament.compose.scene.PostProcessing
import io.github.erkko68.filament.compose.scene.RenderQuality
import io.github.erkko68.filament.compose.scene.LinearColor
import io.github.erkko68.filament.compose.scene.Group
import io.github.erkko68.filament.compose.scene.PointLight
import io.github.erkko68.filament.compose.scene.Direction
import io.github.erkko68.filament.compose.scene.Position
import io.github.erkko68.filament.compose.scene.Rotation
import io.github.erkko68.filament.compose.scene.LightIntensity
import io.github.erkko68.filament.compose.scene.Projection
import io.github.erkko68.filament.compose.scene.primitives.Sphere
import io.github.erkko68.filament.compose.scene.Skybox
import io.github.erkko68.filament.compose.scene.rememberCameraState
import io.github.erkko68.filament.compose.scene.rememberColorMaterialInstance
import io.github.erkko68.filament.compose.scene.rememberEmissiveMaterialInstance
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

private data class Planet(
    val color: LinearColor,
    val radius: Float,
    val orbitRadius: Float,
    val orbitSpeed: Float, // radians per second
    val spinSpeed: Float,  // radians per second around its own Y axis
)

private val PLANETS = listOf(
    Planet(LinearColor(0.65f, 0.65f, 0.70f), radius = 0.20f, orbitRadius = 1.8f, orbitSpeed = 1.6f, spinSpeed = 4.0f), // mercury
    Planet(LinearColor(0.95f, 0.65f, 0.30f), radius = 0.32f, orbitRadius = 2.7f, orbitSpeed = 1.2f, spinSpeed = 2.5f), // venus
    Planet(LinearColor(0.25f, 0.55f, 0.95f), radius = 0.36f, orbitRadius = 3.8f, orbitSpeed = 0.9f, spinSpeed = 3.0f), // earth
    Planet(LinearColor(0.85f, 0.30f, 0.20f), radius = 0.26f, orbitRadius = 5.0f, orbitSpeed = 0.6f, spinSpeed = 2.0f), // mars
)

/**
 * Demonstrates `Group { }`, `Light` grouping, and `rememberSceneClock()` together: a small
 * solar system. The sun bobs up and down on the Y axis; each planet orbits the sun and spins
 * on its own axis. Every animated transform is just `time` plugged into `sin`/`cos`.
 */
@Composable
fun SolarScene(onBack: () -> Unit) {
    val cameraState = rememberCameraState(
        initialEye        = Position(0f, 4f, 9f),
        initialTarget     = Position(0f, 0f, 0f),
        initialProjection = Projection.Perspective(fovDegrees = 50.0),
    )
    val orbit  = rememberOrbitCameraController(cameraState)
    val time by rememberSceneClock()

    // RenderQuality(HIGH) forces an FP16 HDR color buffer so emissive values above 1.0 survive
    // without clipping; Dithering masks any residual 8-bit step at the output. A larger bloom
    // resolution + 7 mip levels keeps the halo smooth on hi-DPI retina displays where the
    // default ~360 px chain would otherwise look pixelated.
    val postProcessing = PostProcessing(
        renderQuality = RenderQuality(),
        dithering     = Dithering(),
        bloom         = Bloom(strength = 0.6f, resolution = 480, levels = 7),
        antiAliasing  = AntiAliasing(fxaaEnabled = true),
    )

    Box(Modifier.fillMaxSize()) {
        FilamentSceneView(
            modifier = Modifier
                .fillMaxSize()
                .orbitGestures(orbit),
            cameraState = cameraState,
            postProcessing = postProcessing,
        ) {
            Skybox(color = LinearColor(0.02f, 0.03f, 0.06f))
            // Sun assembly — the Group's transform moves the emissive sphere *and* the point
            // light together. The built-in UNLIT emissive material makes the sphere appear
            // self-illuminated regardless of scene lighting; bloom turns the emissive output
            // into the halo around the sun.
            val sunY = sin(time * 0.6f) * 0.6f
            Group(position = Position(0f, sunY, 0f)) {
                Sphere(
                    material = rememberEmissiveMaterialInstance(
                        color     = LinearColor(1.0f, 0.85f, 0.40f),
                        intensity = 4.0f,
                    ),
                    radius = 0.7f,
                )
                PointLight(
                    color     = LinearColor(1f, 0.85f, 0.5f),
                    intensity = LightIntensity.LuminousPower(400_000f),
                    falloff   = 15f,
                    position  = Position(0f, 0f, 0f),
                )
            }

            // Planets — each one is a Group at the orbit angle, containing a Sphere that
            // spins in its own local frame. Two nested transforms doing two animations.
            PLANETS.forEachIndexed { i, p ->
                val orbitAngle = time * p.orbitSpeed + (i * PI.toFloat() / 2f)
                val spinDegrees = (time * p.spinSpeed) * (180f / PI.toFloat())
                val px = cos(orbitAngle) * p.orbitRadius
                val pz = sin(orbitAngle) * p.orbitRadius
                Group(position = Position(px, 0f, pz)) {
                    Sphere(
                        material = rememberColorMaterialInstance(p.color),
                        radius   = p.radius,
                        rotation = Rotation.axisAngle(Direction(0f, 1f, 0f), spinDegrees),
                    )
                }
            }
        }
        BackButton(onClick = onBack, modifier = Modifier.align(Alignment.TopStart))
    }
}
